import { isEqual } from 'lodash';

import { getAttributeValueKey } from 'companySettings/components/PartFinderAttributeValues/utils';
import { AttributeType } from 'companySettings/enums';
import {
  IPartFinderAttributeValue,
  IPartFinderProductType,
  IPartFinderProductTypeAttribute,
} from 'companySettings/types';

// What a save would send, so two attribute lists compare by content and order only.
const normalizeAttributes = (attributes: IPartFinderProductTypeAttribute[]) =>
  (attributes ?? []).map((attribute) => ({
    id: attribute.id ?? null,
    name: (attribute.name ?? '').trim(),
    type: attribute.type,
    sourceAttributeId: attribute.sourceAttributeId ?? null,
    updateList: Boolean(attribute.updateList),
    values: (attribute.values ?? []).map((value) => ({
      value: value.value ?? null,
      colorHex: value.colorHex ?? null,
      rangeMin: value.rangeMin ?? null,
      rangeMax: value.rangeMax ?? null,
      rangeIncrement: value.rangeIncrement ?? null,
      rangeUnit: value.rangeUnit ?? null,
    })),
  }));

// A new product type always has something to save; an edited one only once its name, its
// attributes, their values or their order differ from what was loaded.
export const hasProductTypeChanges = (
  name: string,
  attributes: IPartFinderProductTypeAttribute[],
  productType?: IPartFinderProductType
) =>
  !productType ||
  (name ?? '').trim() !== productType.name ||
  !isEqual(normalizeAttributes(attributes), normalizeAttributes(productType.attributes));

export const moveItem = <T>(items: T[], from: number, to: number): T[] => {
  const next = [...items];
  const [moved] = next.splice(from, 1);

  next.splice(to, 0, moved);

  return next;
};

// A value typed on top of a list is "new" until the list itself carries it.
export const isValueInList = (
  type: AttributeType,
  value: IPartFinderAttributeValue,
  listValues: IPartFinderAttributeValue[]
) =>
  listValues.some(
    (listValue) => getAttributeValueKey(type, listValue) === getAttributeValueKey(type, value)
  );

// Only the columns a value is stored as: what an attribute copies from a list, a list created
// from an attribute starts with, and a save sends.
export const copyListValues = (
  listValues: IPartFinderAttributeValue[]
): IPartFinderAttributeValue[] =>
  (listValues ?? []).map(({ value, colorHex, rangeMin, rangeMax, rangeIncrement, rangeUnit }) => ({
    ...(value !== undefined && value !== null && { value }),
    ...(colorHex && { colorHex }),
    ...(rangeMin !== undefined && rangeMin !== null && { rangeMin }),
    ...(rangeMax !== undefined && rangeMax !== null && { rangeMax }),
    ...(rangeIncrement && { rangeIncrement }),
    ...(rangeUnit && { rangeUnit }),
  }));
